import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ToastMessageLevel } from 'views/components/atoms/toast';
import { SendSMSParam } from 'views/components/templates/sendSMS';
import {
  GetListAuthsQuery,
  GetNegotiateTokenDocument,
  useGetAccountInfoQuery,
  useGetListAuthsQuery,
  useSendSmsMutation,
  useSmsCertificateMutation,
  useUpdateMfaSetupStatusMutation,
} from 'views/utils/graphql/graphql';
import { authenticationCodeValidation } from 'views/utils/commonValidation';
import useOnceSubmit from 'views/utils/useOnceSubmit';
import { useToast } from 'views/components/atoms/toast/hook';
import { useClient } from 'urql';
import {
  SignGateCodeVerifierKey,
  SignGateRequestBody,
  SignGateResponse,
  SignGateStateKey,
  fetchDataWithAuthorization,
  generatePkceCodeChallenge,
  generatePkceCodeVerifier,
  generateRandomNumericString,
  isSignGateSuccess,
} from 'views/constants/const/SignGate';
import { MFASetupStatus } from 'views/constants/enums/accountInfo';

export type PasskeyAuth = NonNullable<
  NonNullable<NonNullable<GetListAuthsQuery['GetListAuths']>['AvailableAuthList']>[number]
>;

export enum PasskeyPageStep {
  Normal, // パスキー管理を表示
  SignUpAuth, // パスキー登録前の2要素認証
  SignOutAuth, // パスキー削除前の2要素認証
}

export const useSecuritySetting = () => {
  const [getListAuthsState, getListAuths] = useGetListAuthsQuery({
    requestPolicy: 'network-only',
  });

  const passkeyList: PasskeyAuth[] =
    getListAuthsState.data?.GetListAuths?.AvailableAuthList?.filter((auth): auth is PasskeyAuth => auth !== null) ?? [];

  return {
    getListAuthsState,
    getListAuths,
    passkeyList,
  };
};

export const usePasskeyManage = () => {
  const [passkeyPageStep, setPasskeyPageStep] = useState<PasskeyPageStep>(PasskeyPageStep.Normal);
  const [provisionalToken, setProvisionalToken] = useState<string>('');
  const [deleteCredentialID, setDeleteCredentialID] = useState<string | undefined>('');

  const { toastProps, showToast } = useToast();
  const { onSendSMS } = useSendSMS(setProvisionalToken, passkeyPageStep, setPasskeyPageStep, showToast);

  const initPasskeyPageStep = () => {
    setPasskeyPageStep(PasskeyPageStep.Normal);
    setDeleteCredentialID(undefined);
  };
  const onClickSignUp = async () => {
    setPasskeyPageStep(PasskeyPageStep.SignUpAuth);
    await onSendSMS();
  };

  const onClickSignOut = async (credentialID: string) => {
    setDeleteCredentialID(credentialID);
    setPasskeyPageStep(PasskeyPageStep.SignOutAuth);
    await onSendSMS();
  };

  const authPageProps = useAuthPage(
    provisionalToken,
    deleteCredentialID,
    passkeyPageStep,
    setPasskeyPageStep,
    showToast,
  );

  return {
    passkeyPageStep,
    initPasskeyPageStep,
    onClickSignUp,
    onClickSignOut,
    authPageProps: {
      ...authPageProps,
      onSendSMS,
    },
    toastProps,
  };
};

//認証コード送信
const useSendSMS = (
  setProvisionalToken: React.Dispatch<React.SetStateAction<string>>,
  pageStep: PasskeyPageStep,
  setPageStep: React.Dispatch<React.SetStateAction<PasskeyPageStep>>,
  onShowToast: (label: string, messageLevel: ToastMessageLevel) => void,
) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [sendSMSData, sendSMS] = useSendSmsMutation();
  const onSendSMS = useOnceSubmit<void>(
    {
      onSubmit: async () => {
        const res = await sendSMS({
          input: {
            Type: 2,
          },
        });

        if (res.data && res.data.SendSMS?.Code !== 0) {
          onShowToast(res.data?.SendSMS?.Message ?? '認証コードの送信に失敗しました', ToastMessageLevel.Error);
        } else if (res.data?.SendSMS?.Code === 0) {
          setProvisionalToken(res.data.SendSMS.ProvisionalToken!);
          if (pageStep === PasskeyPageStep.SignUpAuth || pageStep === PasskeyPageStep.SignOutAuth) {
            onShowToast('認証コードを送信しました', ToastMessageLevel.Info);
          }
        }
      },
    },
    [sendSMS, setProvisionalToken, pageStep, setPageStep, onShowToast],
  );

  return {
    onSendSMS,
  };
};

//認証ページ
const useAuthPage = (
  provisionalToken: string,
  deleteCredentialID: string | undefined,
  pageStep: PasskeyPageStep,
  setPageStep: React.Dispatch<React.SetStateAction<PasskeyPageStep>>,
  showToast: (label: string, mesageLevel: ToastMessageLevel) => void,
) => {
  const [searchParams] = useSearchParams();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [accountInfo, getAccountInfo] = useGetAccountInfoQuery({
    requestPolicy: 'network-only',
    pause: false,
  });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [smsCertificatelData, smsCertificate] = useSmsCertificateMutation();
  const [, updateMfaSetupStatus] = useUpdateMfaSetupStatusMutation();
  const client = useClient();

  const last4Digits = sessionStorage.getItem('last_4_digits') || '';
  const phoneNumber = last4Digits;

  const validate = (values: SendSMSParam) => {
    const errors: Partial<{
      authenticationCode: string;
    }> = {};

    const codeError = authenticationCodeValidation(values.authenticationCode);
    if (codeError) {
      errors.authenticationCode = codeError;
    }

    return errors;
  };

  const onSubmit = useOnceSubmit<SendSMSParam>(
    {
      onSubmit: async (values) => {
        const certificateRes = await smsCertificate({
          input: {
            Type: 2,
            AuthenticationCode: values.authenticationCode,
            ProvisionalToken: provisionalToken,
          },
        });

        if (certificateRes.data && certificateRes.data.SMSCertificate?.Code !== 0) {
          showToast(certificateRes.data?.SMSCertificate?.Message ?? '認証に失敗しました', ToastMessageLevel.Error);
          return;
        }

        // SpotPathのSignGate type=sign_upをたたいて遷移！
        const account = accountInfo.data?.GetAccountInfo?.Account!;
        const negotiateRes = await client.query(
          GetNegotiateTokenDocument,
          { account },
          { requestPolicy: 'network-only' },
        );

        const negotiateToken = negotiateRes.data?.GetNegotiateToken?.NegotiateToken;

        if (negotiateRes.data && negotiateRes.data.GetNegotiateToken?.Code !== 0) {
          showToast(negotiateRes.data.GetNegotiateToken?.Message ?? 'ログインに失敗しました', ToastMessageLevel.Error);
          return;
        }

        const url = process.env.REACT_APP_MFA_URL + '/common/sign_gate';
        const state = generateRandomNumericString(6);
        const codeVerifier = generatePkceCodeVerifier();
        const codeChallenge = await generatePkceCodeChallenge(codeVerifier);

        const securitySettingUrl = `${process.env.REACT_APP_SCHEME_FOR_MFA}/securitySetting?tab=1`;
        let requestBody: SignGateRequestBody;
        switch (pageStep) {
          // 新規登録のパラメータ
          case PasskeyPageStep.SignUpAuth:
            // 登録時のみ、リダイレクト後の検証用に保存
            sessionStorage.setItem(SignGateStateKey, state);
            sessionStorage.setItem(SignGateCodeVerifierKey, codeVerifier);
            requestBody = {
              action: 'sign_up',
              auth_flow: 'passkey',
              state,
              code_challenge: codeChallenge,
              client_disp_name: 'FPG証券',
              user_disp_name: account,
              redirect_url: securitySettingUrl,
              response_url_on_fail: securitySettingUrl,
            };
            break;
          case PasskeyPageStep.SignOutAuth:
            // 削除時はstateとcode verifierを保持しない
            sessionStorage.removeItem(SignGateStateKey);
            sessionStorage.removeItem(SignGateCodeVerifierKey);
            requestBody = {
              action: 'sign_out',
              auth_flow: 'passkey',
              state,
              credential_id: deleteCredentialID,
              code_challenge: codeChallenge,
              client_disp_name: 'FPG証券',
              user_disp_name: account,
              redirect_url: securitySettingUrl,
              response_url_on_fail: securitySettingUrl,
            };
            break;
          default:
            return;
        }

        const res = await fetchDataWithAuthorization<SignGateResponse>(url, negotiateToken, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestBody),
        });

        if (isSignGateSuccess(res)) {
          window.location.assign(res.redirect_url);
          return;
        }
        console.log(res);
        showToast(res.error_message ?? '時間をおいてからやり直してください', ToastMessageLevel.Error);
      },
      waitMilliSecondsForHideLoading: 0,
    },
    [provisionalToken, showToast, setPageStep],
  );

  const formik = useFormik<SendSMSParam>({
    initialValues: {
      authenticationCode: '',
    },
    onSubmit,
    validate,
    validateOnBlur: false,
    validateOnChange: false,
  });

  useEffect(() => {
    formik.setFieldValue('authenticationCode', '');
  }, []);

  useEffect(() => {
    const savedState = sessionStorage.getItem(SignGateStateKey);
    const codeVerifier = sessionStorage.getItem(SignGateCodeVerifierKey);

    if (!savedState || !codeVerifier) {
      return;
    }

    const code = searchParams.get('code');
    const state = searchParams.get('state');

    // SignGateから返却された認可コードとstateを検証し、PKCEフローが正常に完了した場合だけ登録完了として扱う
    if (!code || !state || state !== savedState) {
      sessionStorage.removeItem(SignGateStateKey);
      sessionStorage.removeItem(SignGateCodeVerifierKey);
      showToast('パスキーの登録に失敗しました。もう一度お試しください。', ToastMessageLevel.Error);
      return;
    }

    if (accountInfo.fetching) {
      return;
    }

    const updateMfaSetupStatusToCompleted = async () => {
      try {
        if (accountInfo.data?.GetAccountInfo?.MFASetupStatus === MFASetupStatus.NotSetup) {
          const res = await updateMfaSetupStatus({
            input: {
              Status: MFASetupStatus.Completed,
            },
          });

          if (res.data?.UpdateMFASetupStatus?.Code === 0) {
            getAccountInfo();
          }
        }
      } finally {
        sessionStorage.removeItem(SignGateStateKey);
        sessionStorage.removeItem(SignGateCodeVerifierKey);
      }
    };

    updateMfaSetupStatusToCompleted();
  }, [
    accountInfo.data?.GetAccountInfo?.MFASetupStatus,
    accountInfo.fetching,
    getAccountInfo,
    searchParams,
    showToast,
    updateMfaSetupStatus,
  ]);

  return { formik, phoneNumber };
};
